import { useState, useEffect } from "react";
import axios from "axios";
import { Copy, Check, QrCode, Mail, Users, X, Printer } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function EducatorQuickShare() {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    try {
      const res = await axios.get(`${API}/classes`);
      const list = res.data || [];
      setClasses(list);
      if (list.length > 0) setSelectedClassId(list[0].id);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  if (loading) return null;

  const selectedClass = classes.find((c) => c.id === selectedClassId);
  const joinCode = selectedClass?.join_code || "";
  const shareLink = joinCode
    ? `${window.location.origin}/login?code=${joinCode}&path=middle_school`
    : "";
  const qrUrl = shareLink
    ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(shareLink)}`
    : "";

  const copyLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* ignore */ }
  };

  const emailSubject = encodeURIComponent("Join our Middle School Entrepreneurship class!");
  const emailBody = encodeURIComponent(
    `Hi! I'd like to invite your student to join our Lemonade Stand Business Plan class — Middle School path.\n\n` +
      `Class Code: ${joinCode}\nClick to join: ${shareLink}\n\n` +
      `No email signup required — students just enter the class code and a first name.`
  );

  const openPrintablePoster = async () => {
    if (!selectedClassId) return;
    try {
      const token = localStorage.getItem("token");
      const url = `${API}/classes/${selectedClassId}/qr-poster?path=middle_school&base_url=${encodeURIComponent(window.location.origin)}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load poster");
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
      // Revoke after a delay so the new tab can load it
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Could not generate poster. Please try again.");
    }
  };

  return (
    <div
      className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-2xl p-6 mb-8"
      data-testid="educator-quick-share"
    >
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-5 h-5 text-indigo-600" />
        <h3 className="text-lg font-bold text-gray-800">Educator Quick-Share</h3>
        <span className="ml-auto text-xs font-semibold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
          For Teachers
        </span>
      </div>

      {classes.length === 0 ? (
        <p className="text-sm text-gray-600">
          You don't have any classes yet. Create one to share a Middle School join link with your younger students.
        </p>
      ) : (
        <>
          <p className="text-sm text-gray-700 mb-4">
            Share this link with younger learners — it auto-fills the class code and lands on the Middle School path. No email signup required.
          </p>

          {/* Class selector */}
          {classes.length > 1 && (
            <div className="mb-4">
              <label htmlFor="qs-class" className="block text-xs font-semibold text-gray-700 mb-1">
                Class
              </label>
              <select
                id="qs-class"
                value={selectedClassId}
                onChange={(e) => setSelectedClassId(e.target.value)}
                className="w-full px-3 py-2 border-2 border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500 bg-white"
                data-testid="qs-class-select"
              >
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.join_code})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Share link row */}
          <div className="bg-white rounded-lg border-2 border-indigo-200 p-3 mb-3 flex items-center gap-2 flex-wrap">
            <code
              className="flex-1 min-w-0 text-xs sm:text-sm text-gray-800 truncate"
              data-testid="qs-share-link"
              title={shareLink}
            >
              {shareLink}
            </code>
            <button
              onClick={copyLink}
              className="flex items-center gap-1 px-3 py-1.5 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold rounded-lg transition-colors"
              data-testid="qs-copy-link-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowQR(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-white border-2 border-indigo-300 text-indigo-700 text-sm font-semibold rounded-lg hover:bg-indigo-50"
              data-testid="qs-show-qr-btn"
            >
              <QrCode className="w-4 h-4" /> Show QR Code
            </button>
            <a
              href={`mailto:?subject=${emailSubject}&body=${emailBody}`}
              className="flex items-center gap-1.5 px-4 py-2 bg-white border-2 border-indigo-300 text-indigo-700 text-sm font-semibold rounded-lg hover:bg-indigo-50"
              data-testid="qs-email-link"
            >
              <Mail className="w-4 h-4" /> Email Invite
            </a>
            <button
              onClick={openPrintablePoster}
              className="flex items-center gap-1.5 px-4 py-2 bg-white border-2 border-indigo-300 text-indigo-700 text-sm font-semibold rounded-lg hover:bg-indigo-50"
              data-testid="qs-print-poster-btn"
              title="Open a printable PDF poster with QR code and class code"
            >
              <Printer className="w-4 h-4" /> Print Poster
            </button>
            <span
              className="px-3 py-2 text-xs text-gray-500 italic"
              data-testid="qs-class-code-display"
            >
              Class code: <strong className="text-gray-800 tracking-wider">{joinCode}</strong>
            </span>
          </div>
        </>
      )}

      {/* QR Modal */}
      {showQR && qrUrl && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setShowQR(false)}
          data-testid="qs-qr-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-gray-800">Scan to join</h4>
              <button
                onClick={() => setShowQR(false)}
                className="text-gray-400 hover:text-gray-700"
                data-testid="qs-qr-close-btn"
                aria-label="Close QR code modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <img
              src={qrUrl}
              alt={`QR code linking to ${shareLink}`}
              className="mx-auto mb-3 rounded-lg border-2 border-gray-200"
              data-testid="qs-qr-image"
            />
            <div className="text-xs text-gray-500 mb-1">Class Code</div>
            <div className="text-2xl font-bold text-indigo-700 tracking-widest mb-2">{joinCode}</div>
            <p className="text-xs text-gray-600">
              Students scan this code, enter their first name, and they're in the Middle School path.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default EducatorQuickShare;
